import React, { useState, useEffect, useRef } from 'react';
import Swal from 'sweetalert2';
import HeroSection from './components/HeroSection';
import SpecialtySelection from './components/SpecialtySelection';
import BookingMethodSelector from './components/BookingMethodSelector';
import DoctorSelection from './components/DoctorSelection';
import ConditionSelection from './components/ConditionSelection';
import TimeSlotSelection from './components/TimeSlotSelection';
import CalendarSelection from './components/CalendarSelection'; 
import BookingForm from './components/BookingForm';
import ConfirmationScreen from './components/ConfirmationScreen';
import SurveyFlow from './components/SurveyFlow';
import IncludedConsultationConfirm from './components/IncludedConsultationConfirm';
import CheckoutFlow from './components/checkout/CheckoutFlow';
import MembershipCheckout from './components/checkout/MembershipCheckout';
import VerificationFlow from './components/checkout/steps/VerificationFlow';
import AuthModal from './components/AuthModal';
import WelcomeOnboarding from './components/WelcomeOnboarding';
import DoctorDirectoryPage from './components/DoctorDirectoryPage';
import DoctorScheduleFullPage from './components/DoctorScheduleFullPage';
import MatchConfirmationPage from './components/MatchConfirmationPage';
import ScheduleBrowserPage from './components/ScheduleBrowserPage';
import RequestOndemandPage from './components/RequestOndemandPage';
import ProfilePage from './components/ProfilePage';
import OnDemandExplainerModal from './components/OnDemandExplainerModal';
import BookingGateModal from './components/BookingGateModal';
import WhatsappVerificationModal from './components/WhatsappVerificationModal';
import scheduleService from './services/scheduleService';
import { checkEmailAvailability, checkPhoneAvailability, fetchDaisUserByFirebaseUid, fetchUserConsultations, getFreshIdToken } from './services/FirebaseService';
import { checkUserSurveyCompleted } from './services/consultationService';
import specialtyService from './services/specialtyService';
import { doctorService } from './services/doctorService';
import { getUserSession, saveReservation, getReservation, clearSession, saveUserSession, getMembershipPlans, getSpecialtyPrice } from './utils/checkoutHelpers';
import { bootstrapSessionFromBridge } from './utils/sessionBootstrap';
import { pixelPageView, pixelCompleteRegistration, pixelSchedule, pixelPurchase, generateEventId } from './lib/pixel';
import GlobalLoader from './components/GlobalLoader';
import flordaisies from './assets/florDaisiesSF.png';

// Deep-link de entrada: /agendar (o #/agendar) aterriza directo en la
// selección de especialidad, saltándose el hero. Pensado para enlaces de
// plantillas de WhatsApp a usuarias que ya tienen cuenta + membresía y solo
// les falta agendar. El resto del flujo (bookingMethod → checkout/membresía)
// corre igual porque todo vive en el mismo `step` de la ruta main.
const detectInitialStep = () => {
  const path = window.location.pathname;
  const hash = window.location.hash;
  if (path === '/agendar' || hash === '#/agendar') return 'specialty';
  return 'hero';
};

const App = () => {
  const [step, setStep] = useState(detectInitialStep);
  const [selectedSpecialty, setSelectedSpecialty] = useState('');
  const [selectedTimeSlot, setSelectedTimeSlot] = useState('');
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState('');
  const [loading, setLoading] = useState(false);
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [availabilityData, setAvailabilityData] = useState({});
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [campaignSpecialties, setCampaignSpecialties] = useState([]);
  const [campaignDurationMap, setCampaignDurationMap] = useState({});
  const [validationErrors, setValidationErrors] = useState({
    email: '',
    telefono: ''
  });
  
  const [specialties, setSpecialties] = useState([]);
  const [filteredSpecialties, setFilteredSpecialties] = useState([]);
  const [specialtyConfig, setSpecialtyConfig] = useState({});
  const [specialtiesLoading, setSpecialtiesLoading] = useState(true);
  
  // ============ NUEVO ESTADO PARA LOADER GLOBAL ============
  const [globalLoading, setGlobalLoading] = useState(false);
  
  const [leadData, setLeadData] = useState({
    leadId: null,
    userId: null,
    firebaseUid: null,
    authToken: null,
    accessToken: null,
    email: '',
    password: ''
  });
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    nombre: '',
    apellido: '',
    telefono: '',
    phoneCountry: 'MX',
    phoneCode: '+52',
    fechaNacimiento: '',
    sexoNacimiento: '',
    identidadGenero: '',
    campaniaOrigen: 'DefaultCampaign',
    pronoun: '1',
    birthDate: '',
    birthSex: 'female',
    consultationReason: ''
  });

  const consultationReasons = [
    { id: 1, label: 'Ansiedad y estrés crónico' },
    { id: 2, label: 'Dificultad para manejar emociones y reacciones impulsivas' },
    { id: 3, label: 'Problemas de autoestima y confianza' },
    { id: 4, label: 'Duelos y procesos de pérdida' },
    { id: 5, label: 'Dificultades en relaciones de pareja o familiares' }
  ];

  const [bookingMethod, setBookingMethod] = useState('');
  const API_URL = 'https://odoo.core.daisies.mx/odoo-firebase-core/odoo-import';
  const [utmParams, setUtmParams] = useState({
    campaign: 'DefaultCampaign',
    medium: 'Website',
    source: 'Website'
  });

  const [paymentResult, setPaymentResult] = useState(null);
  const [loggedInUser, setLoggedInUser] = useState(null);
  const [savedReservation, setSavedReservation] = useState(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [userConsultations, setUserConsultations] = useState([]);
  const [selectedMembership, setSelectedMembership] = useState(null);
  const [pendingSurveyId, setPendingSurveyId] = useState(null);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [selectedOndemandDoctor, setSelectedOndemandDoctor] = useState(null);

  // ─── Estado del gate híbrido (login antes de elegir slot) ───
  // pendingSlotForBooking guarda el slot que el usuario tocó cuando no estaba
  // logueado. Tras el auth exitoso, lo retomamos automáticamente.
  const [pendingSlotForBooking, setPendingSlotForBooking] = useState(null);

  // ─── Modal explainer on-demand (matchea OnDemandExplainerSheet de Flutter) ───
  // Si el slot devuelve booking_mode='on_demand' se interpone este modal antes
  // de que la usuaria entre al checkout / membership flow.
  const [onDemandSlotPending, setOnDemandSlotPending] = useState(null);

  // ─── Modal-gate de pre-booking (replica el orchestrated_slot_sheet de Flutter) ───
  // Se llena cuando `validate_schedule_include_consultation` devuelve
  // `success: false` y necesitamos ofrecer "comprar membresía" o "pagar extra"
  // (o "comprar minutos" si la usuaria ya tiene membresía pero sin saldo).
  const [bookingGate, setBookingGate] = useState(null); // { slot, validation }

  // ─── Gate V2 de verificación de teléfono — bloquea ANTES de reservar slot ───
  // Si la usuaria todavía no verificó su WhatsApp (phone_verified == false),
  // no debe poder bloquear ningún horario. Guardamos el slot pendiente para
  // retomarlo automáticamente tras la verificación.
  const [phoneVerificationGate, setPhoneVerificationGate] = useState(null); // { slot }

  // ============ ROUTING (pathname + hash fallback) ============
  const detectRoute = () => {
    const path = window.location.pathname;
    const hash = window.location.hash;
    if (path === '/ahora' || hash === '#/ahora') return 'ahora';
    if (path === '/horarios' || hash === '#/horarios') return 'horarios';
    if (path === '/horarios-doctora' || hash === '#/horarios-doctora') return 'horarios-doctora';
    if (path === '/agendar-match' || hash === '#/agendar-match') return 'agendar-match';
    if (path === '/perfil' || hash === '#/perfil') return 'perfil';
    return 'main';
  };

  const [currentRoute, setCurrentRoute] = useState(detectRoute);

  useEffect(() => {
    const handleRouteChange = () => setCurrentRoute(detectRoute());
    window.addEventListener('hashchange', handleRouteChange);
    // Maneja el back/forward del navegador para mantener currentRoute sincronizado
    // con la URL. Crítico para los routes /agendar-match y /horarios-doctora,
    // donde la usuaria puede navegar atrás sin perder el routing en memoria.
    window.addEventListener('popstate', handleRouteChange);
    return () => {
      window.removeEventListener('hashchange', handleRouteChange);
      window.removeEventListener('popstate', handleRouteChange);
    };
  }, []);

  // Meta Pixel: PageView por navegación. Esta SPA no usa react-router;
  // currentRoute es la fuente de verdad del routing. El PageView del primer
  // load lo dispara initPixel() en index.js, por eso se salta el valor inicial.
  const lastPixelRoute = useRef(currentRoute);
  useEffect(() => {
    if (lastPixelRoute.current === currentRoute) return;
    lastPixelRoute.current = currentRoute;
    pixelPageView();
  }, [currentRoute]);

  // Restaurar sesión al montar + enriquecer con datos de Firestore
  useEffect(() => {
    const restoreSession = async () => {
      // Si la usuaria viene de landing autenticada (cookies en .daisies.mx o
      // URL params en dev), promovemos esa sesión a localStorage antes de
      // que getUserSession() decida si hay algo que restaurar.
      try { bootstrapSessionFromBridge(); } catch (e) { console.warn('[App] sessionBootstrap failed:', e); }
      const session = getUserSession();
      if (!session) return;

      let enrichedSession = { ...session };

      // Enriquecer con datos de dais_user si tenemos firebaseUid
      if (session.firebaseUid) {
        try {
          const daisUser = await fetchDaisUserByFirebaseUid(session.firebaseUid);

          if (daisUser) {
            enrichedSession = {
              ...enrichedSession,
              nombre: daisUser.name || session.nombre,
              apellido: daisUser.last_name || session.apellido,
              email: daisUser.email || session.email,
              telefono: daisUser.phone || session.telefono,
              avatar: daisUser.avatar || '',
              daisUserId: daisUser.id || null,
              stepState: daisUser.step_state || '',
              membership: daisUser.membership || null,
              membership_active: daisUser.membership_active || '',
              inscription: daisUser.inscription || null,
              inscription_active: daisUser.inscription_active || '',
              balance: daisUser.balance ?? null,
              phoneVerified: session?.phoneVerified === true || daisUser.phone_verified === true,
            };

            // Obtener consultas del usuario
            if (daisUser.id) {
              const consultations = await fetchUserConsultations(daisUser.id);
              setUserConsultations(consultations);
            }
          }
        } catch (err) {
          console.warn('Error enriching session from Firestore:', err);
        }
      }

      setLoggedInUser(enrichedSession);
      setSavedReservation(getReservation());

      // Pre-llenar formData con datos del perfil para no repetir el formulario
      setFormData(prev => ({
        ...prev,
        nombre: enrichedSession.nombre || prev.nombre,
        apellido: enrichedSession.apellido || prev.apellido,
        email: enrichedSession.email || prev.email,
        telefono: enrichedSession.telefono || prev.telefono,
        phoneCountry: enrichedSession.phoneCountry || prev.phoneCountry,
        phoneCode: enrichedSession.phoneCode || prev.phoneCode,
        birthDate: enrichedSession.birthDate || prev.birthDate,
        birthSex: enrichedSession.birthSex || prev.birthSex,
        pronoun: enrichedSession.pronoun || prev.pronoun
      }));

      setLeadData(prev => ({
        ...prev,
        leadId: enrichedSession.leadId || prev.leadId,
        userId: enrichedSession.userId || prev.userId,
        firebaseUid: enrichedSession.firebaseUid || prev.firebaseUid,
        authToken: enrichedSession.authToken || prev.authToken,
        email: enrichedSession.email || prev.email
      }));
    };

    restoreSession();
  }, []);

  // Cargar especialidades
  useEffect(() => {
    const fetchSpecialties = async () => {
      try {
        setSpecialtiesLoading(true);
        
        const [uiSpecialties, config] = await Promise.all([
          specialtyService.getSpecialtiesForUI(),
          specialtyService.getSpecialtyConfig()
        ]);
        
        
        setSpecialtyConfig(config);
        setSpecialties(uiSpecialties);
        setFilteredSpecialties(uiSpecialties);
        
      } catch (error) {
        
        const fallbackSpecialties = [
          { id: 16, name: 'Psicología', icon: '', incidence: 'high', odooId: 16, firebaseId: '16', durationId: 109 },
          { id: 15, name: 'Ginecología', icon: '', incidence: 'high', odooId: 15, firebaseId: '15', durationId: 1 },
          { id: 17, name: 'Nutrición', icon: '', incidence: 'medium', odooId: 17, firebaseId: '17', durationId: 30 },
          { id: 18, name: 'Salud General', icon: '', incidence: 'medium', odooId: 18, firebaseId: '18', durationId: 28 }
        ];
        
        const fallbackConfig = {};
        fallbackSpecialties.forEach(spec => {
          fallbackConfig[spec.id] = {
            id: spec.odooId,
            duration: spec.durationId,
            name: spec.name,
            icon: spec.icon,
            firebaseId: spec.firebaseId
          };
        });
        
        setSpecialtyConfig(fallbackConfig);
        setSpecialties(fallbackSpecialties);
        setFilteredSpecialties(fallbackSpecialties);
      } finally {
        setSpecialtiesLoading(false);
      }
    };
    
    fetchSpecialties();
  }, []);

  // Cargar UTM params
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    
    const hasUtmParams = 
      urlParams.has('utm_campaign') || 
      urlParams.has('utm_medium') || 
      urlParams.has('utm_source');
    
    let utmCampaign, utmMedium, utmSource;
    
    if (!hasUtmParams) {
      utmCampaign = 'DefaultCampaign';
      utmMedium = 'Website';
      utmSource = 'Website';
    } else {
      utmCampaign = urlParams.get('utm_campaign') || 'DefaultCampaign';
      utmMedium = urlParams.get('utm_medium') || 'Website';
      utmSource = urlParams.get('utm_source') || 'Website';
    }

    setUtmParams({
      campaign: utmCampaign,
      medium: utmMedium,
      source: utmSource
    });

    setFormData(prev => ({
      ...prev,
      campaniaOrigen: utmCampaign
    }));

    fetchCampaignSpecialties(utmCampaign, utmMedium, utmSource);
  }, []);

  const fetchCampaignSpecialties = async (campaign, medium, source) => {
    try {
      
      const searchResponse = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          params: {
            res_model: "utm.campaign",
            res_method: "search_read",
            res_id: 1,
            res_params: {
              campaign_name: campaign
            }
          }
        })
      });

      const searchData = await searchResponse.json();
      
      let campaignId = null;
      
      if (searchData.result?.success && searchData.result?.data?.campaign_id) {
        campaignId = searchData.result.data.campaign_id;
      } else {
        if (campaign === 'DefaultCampaign') {
          campaignId = 36;
        }
      }

      if (!campaignId) {
        throw new Error(`No se pudo encontrar la campaña: ${campaign}`);
      }

      const response = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          params: {
            res_model: "utm.campaign",
            res_method: "get_campaign_record",
            res_id: campaignId,
            res_params: {
              utm_campaign: campaign,
              utm_source: source,
              utm_medium: medium
            }
          }
        })
      });

      const data = await response.json();
      
      if (data.result?.success && data.result?.data?.specialties) {
        const campaignSpecs = data.result.data.specialties;
        setCampaignSpecialties(campaignSpecs);
        
        // Build campaign duration map: specialty_id -> { includedDurationId, extraDurationId }
        const durationMap = {};
        campaignSpecs.forEach(cs => {
          // cs.duration is the campaign's default (could be either included or extra)
          // cs.specialty.durations = all standard durations for this specialty
          // cs.specialty.extra_durations = all extra durations for this specialty
          
          const standardDurations = cs.specialty.durations || [];
          const extraDurations = cs.specialty.extra_durations || [];
          
          // Find the included (non-extra) duration — first standard duration that is NOT is_extra_duration
          const includedDur = standardDurations.find(d => !d.is_extra_duration) || standardDurations[0];
          // Find the extra duration — first from extra_durations array
          const extraDur = extraDurations[0] || null;
          
          durationMap[cs.specialty.id] = {
            includedDurationId: includedDur?.id || null,
            extraDurationId: extraDur?.id || null,
            campaignDefaultDurationId: cs.duration?.id || null,
            campaignDefaultIsExtra: cs.duration?.is_extra_duration || false,
            allDurations: standardDurations,
            allExtraDurations: extraDurations,
          };
        });
        setCampaignDurationMap(durationMap);
        
        if (campaignSpecs.length > 0) {
          const campaignOdooIds = campaignSpecs.map(s => s.specialty.id);
          const filtered = specialties.filter(specialty => 
            campaignOdooIds.includes(specialty.odooId)
          );
          setFilteredSpecialties(filtered.length > 0 ? filtered : specialties);
        } else {
          setFilteredSpecialties(specialties);
        }
      } else {
        setCampaignSpecialties([]);
        setCampaignDurationMap({});
        setFilteredSpecialties(specialties);
      }
    } catch (error) {
      setCampaignSpecialties([]);
      setCampaignDurationMap({});
      setFilteredSpecialties(specialties);
    }
  };

  useEffect(() => {
    if (campaignSpecialties.length > 0 && specialties.length > 0) {
      const campaignOdooIds = campaignSpecialties.map(s => s.specialty.id);
      const filtered = specialties.filter(specialty => 
        campaignOdooIds.includes(specialty.odooId)
      );
      setFilteredSpecialties(filtered.length > 0 ? filtered : specialties);
    } else {
      setFilteredSpecialties(specialties);
    }
  }, [specialties, campaignSpecialties]);

  const getSpecialtyConfig = () => {
    if (!selectedSpecialty) {
      return null;
    }

    const specialtyId = parseInt(selectedSpecialty);

    if (isNaN(specialtyId)) {
      return null;
    }

    // Start with specialty config from specialtyService (Firebase-based)
    let config = specialtyConfig[specialtyId];

    if (!config) {
      const specialty = specialties.find(spec => spec.id === specialtyId);
      if (specialty) {
        config = {
          id: specialty.odooId,
          duration: specialty.durationId || 109,
          extraDuration: specialty.extraDurationId || null,
          name: specialty.name,
          icon: specialty.icon,
          firebaseId: specialty.firebaseId
        };
      }
    }

    // No fallback hardcodeado: si no encontramos la especialidad, devolvemos null y dejamos
    // que el caller maneje el caso. El fallback antiguo a Psicología (id 16) hacía que clics
    // en especialidades sin config (ej. dermatología) terminaran agendando otra especialidad.
    if (!config) {
      console.warn('[App] getSpecialtyConfig: no se encontró config para especialidad', specialtyId);
      return null;
    }

    // Enrich with campaign duration data (overrides Firebase if available)
    const campaignDurations = campaignDurationMap[config.id];
    if (campaignDurations) {
      return {
        ...config,
        duration: campaignDurations.includedDurationId || config.duration,
        extraDuration: campaignDurations.extraDurationId || config.extraDuration || null,
        allDurations: campaignDurations.allDurations || [],
        allExtraDurations: campaignDurations.allExtraDurations || [],
      };
    }
    
    return config;
  };

  const validateForm = async () => {
    const errors = {};

    if (!formData.email.includes('@') || formData.email.length > 100) {
      errors.email = 'El correo debe contener @ y tener máximo 100 caracteres';
    } else {
      const isEmailAvailable = await checkEmailAvailability(formData.email);
      if (!isEmailAvailable) {
        errors.email = 'Este correo electrónico ya está registrado. Por favor usa otro.';
      }
    }

    const passwordRegex = /^(?=.*[A-Za-z])(?=.*\d).{8,}$/;
    if (!passwordRegex.test(formData.password)) {
      Swal.fire({
        icon: 'error',
        title: 'Error en la contraseña',
        html: 'La contraseña debe tener:<br>• Mínimo 8 caracteres<br>• Al menos 1 letra<br>• Al menos 1 número',
        confirmButtonColor: '#786ED9'
      });
      return false;
    }

    const phoneRegex = /^\d{10}$/;
    if (!phoneRegex.test(formData.telefono)) {
      errors.telefono = 'El teléfono debe tener exactamente 10 dígitos';
    } else {
      const isPhoneAvailable = await checkPhoneAvailability(formData.telefono);
      if (!isPhoneAvailable) {
        errors.telefono = 'Este número de teléfono ya está registrado. Por favor usa otro.';
      }
    }

    if (!formData.birthDate) {
      Swal.fire({
        icon: 'error',
        title: 'Fecha de nacimiento requerida',
        text: 'Por favor ingresa tu fecha de nacimiento',
        confirmButtonColor: '#786ED9'
      });
      return false;
    }

    const birthDate = new Date(formData.birthDate);
    const today = new Date();
    const age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    const hasBirthdayPassed = monthDiff > 0 || (monthDiff === 0 && today.getDate() >= birthDate.getDate());
    const actualAge = hasBirthdayPassed ? age : age - 1;

    if (birthDate >= today) {
      Swal.fire({
        icon: 'error',
        title: 'Fecha de nacimiento inválida',
        text: 'La fecha de nacimiento debe ser menor a la fecha actual',
        confirmButtonColor: '#786ED9'
      });
      return false;
    }

    if (actualAge < 18) {
      Swal.fire({
        icon: 'error',
        title: 'Edad insuficiente',
        text: 'Debes ser mayor de 18 años para agendar una cita',
        confirmButtonColor: '#786ED9'
      });
      return false;
    }

    if (!acceptedTerms) {
      Swal.fire({
        icon: 'error',
        title: 'Términos y condiciones',
        text: 'Debes aceptar los términos y condiciones para continuar',
        confirmButtonColor: '#786ED9'
      });
      return false;
    }

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return false;
    }

    return true;
  };

  const handleCompleteMedicalRecord = () => {
    if (!leadData.leadId || !leadData.email || !leadData.password) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No se encontraron los datos necesarios para completar el expediente médico',
        confirmButtonColor: '#786ED9'
      });
      return;
    }

    const params = new URLSearchParams({
      leadId: leadData.leadId.toString(),
      email: leadData.email,
      password: leadData.password
    });

    const redirectUrl = `https://app.daisies.mx/authPage?${params.toString()}`;
    window.location.href = redirectUrl;
  };

  // ============ HELPER: Fetch availability for a single duration_id ============
  const fetchAvailabilityByDuration = async (specialtyId, durationId, date, tz) => {
    const response = await fetch('https://odoo.core.daisies.mx/marketing-manager/import-data', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        params: {
          res_model: "dais.consultation",
          res_method: "get_availble_consultation_blocks_for_externals",
          res_id: 1,
          res_params: {
            specialty_id: specialtyId,
            duration_id: durationId,
            date: date,
            tz: tz,
            utm_campaign: utmParams.campaign || 'DefaultCampaign',
            utm_medium: utmParams.medium || 'Website',
            utm_source: utmParams.source || 'Website'
          }
        }
      })
    });
    return response.json();
  };

  // ============ FUNCIÓN PRINCIPAL: USANDO API PÚBLICA CON LOADER GLOBAL ============
  const fetchAllDoctorsSchedule = async () => {
    const specConfig = getSpecialtyConfig();
    
    if (!specConfig) {
      alert('Por favor selecciona una especialidad válida.');
      return;
    }

    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Mexico_City';

    // ============ ACTIVAR LOADER GLOBAL ============
    setGlobalLoading(true);
    setCalendarLoading(true);
    
    try {
      const today = new Date();
      const todayFormatted = today.toISOString().split('T')[0];
      
      // Build list of ALL unique durations to fetch
      const durationFetchList = []; // { id, isExtra, durationMinutes }
      const seenKeys = new Set();

      // Included durations from campaign or config
      const includedDurations = specConfig.allDurations || [];
      includedDurations.forEach(d => {
        if (d?.id) {
          const key = `${d.id}-${d.is_extra_duration || false}`;
          if (!seenKeys.has(key)) {
            seenKeys.add(key);
            durationFetchList.push({
              id: d.id,
              isExtra: d.is_extra_duration || false,
              durationMinutes: d.duration || null
            });
          }
        }
      });

      // Extra durations from campaign or config
      const extraDurations = specConfig.allExtraDurations || [];
      extraDurations.forEach(d => {
        if (d?.id) {
          const key = `${d.id}-true`;
          if (!seenKeys.has(key)) {
            seenKeys.add(key);
            durationFetchList.push({
              id: d.id,
              isExtra: true,
              durationMinutes: d.duration || null
            });
          }
        }
      });

      // Fallback: if no durations collected, use legacy included + extra
      if (durationFetchList.length === 0) {
        const includedDurationId = specConfig.duration;
        if (includedDurationId) {
          durationFetchList.push({ id: includedDurationId, isExtra: false, durationMinutes: null });
        }
        const extraDurationId = specConfig.extraDuration;
        if (extraDurationId && extraDurationId !== includedDurationId) {
          durationFetchList.push({ id: extraDurationId, isExtra: true, durationMinutes: null });
        }
      }

      console.log('[fetchAllDoctorsSchedule] Fetching via public API for durations:', durationFetchList);

      // Use doctorService to fetch all durations via the public API
      const organized = await doctorService.getAvailabilityByDateAllDurations(
        specConfig.id,
        todayFormatted,
        durationFetchList,
        tz
      );
      
      setAvailabilityData(organized);
      
    } catch (error) {
      console.error('[fetchAllDoctorsSchedule] Error:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error de conexión',
        text: 'No se pudo conectar con el servidor. Por favor intenta de nuevo.',
        confirmButtonColor: '#786ED9'
      });
      setAvailabilityData({});
    } finally {
      // ============ DESACTIVAR LOADER GLOBAL ============
      setGlobalLoading(false);
      setCalendarLoading(false);
    }
  };

  const handleSpecialtySelect = (specialtyId) => {
    
    const idNum = parseInt(specialtyId);
    
    if (isNaN(idNum)) {
      alert('Error al seleccionar especialidad');
      return;
    }
    
    const specialty = specialties.find(spec => spec.id === idNum);
    
    if (!specialty) {
      alert('Error al seleccionar especialidad');
      return;
    }
    
    setSelectedSpecialty(idNum);
    
    
    setStep('bookingMethod');
  };

  const handleBookingMethodSelect = (method) => {
    setBookingMethod(method);

    switch (method) {
      case 'doctor':
        setStep('doctorSelection');
        break;
      case 'date':
        // CalendarSelection ahora hace su propio fetch on-demand al expandir
        // cada día (endpoint unificado create_include_consultation_by_user_v2).
        // No prefetcheamos para evitar dispararlo dos veces.
        setAvailabilityData({});
        setStep('calendar');
        break;
      case 'condition':
        setStep('conditionSelection');
        break;
      default:
        setStep('timeSlot');
    }
  };

  // ─── Helper: check if user has active membership ───
  const userHasActiveMembership = () => {
    if (!loggedInUser) return false;
    return (
      loggedInUser.membership?.state === 'active' ||
      loggedInUser.membership_active === 'active' ||
      loggedInUser.membership_active === true
    );
  };

  // ─── Helper: membership flow — survey check + route ───
  const handleMembershipSlotSelect = async () => {
    const specConfig = getSpecialtyConfig();
    const specialtyId = specConfig?.id || parseInt(selectedSpecialty);
    // Token fresco: el guardado en sesión puede estar vencido (ID tokens
    // expiran a la hora) y haría que el gate diga "no completado" por error.
    const authToken = (await getFreshIdToken()) || loggedInUser.authToken;
    const userId = loggedInUser.daisUserId || loggedInUser.userId;

    if (!authToken || !userId) {
      console.warn('[handleMembershipSlotSelect] Missing authToken or userId, falling back to checkout');
      setStep('checkout');
      return;
    }

    setGlobalLoading(true);
    try {
      const surveyResult = await checkUserSurveyCompleted(authToken, userId, specialtyId);
      console.log('[handleMembershipSlotSelect] Survey check result:', surveyResult);

      if (surveyResult.completed) {
        // Survey already done → go straight to included consultation confirmation
        setStep('includedConfirmation');
      } else {
        // Survey not completed → show survey flow first
        setPendingSurveyId(surveyResult.surveyId);
        setStep('survey');
      }
    } catch (err) {
      console.error('[handleMembershipSlotSelect] Error checking survey:', err);
      // Fallback: go to included confirmation anyway (the API create call will validate)
      setStep('includedConfirmation');
    } finally {
      setGlobalLoading(false);
    }
  };

  // ─── Verifica `phone_verified` con fallback fresco a Firestore ───
  // Devuelve true si la usuaria ya verificó WhatsApp. Si el flag local
  // (`loggedInUser.phoneVerified`) es false, consulta Firestore una vez por
  // si el flag se quedó stale después de una verificación previa.
  const ensurePhoneVerified = async (user) => {
    if (user?.phoneVerified === true) return true;
    if (!user?.firebaseUid) return false;
    try {
      const daisUser = await fetchDaisUserByFirebaseUid(user.firebaseUid);
      const verified = daisUser?.phone_verified === true;
      if (verified) {
        setLoggedInUser(prev => prev ? { ...prev, phoneVerified: true } : prev);
      }
      return verified;
    } catch (err) {
      console.warn('[ensurePhoneVerified] error checking Firestore:', err);
      // Fail-open: si falla la consulta, dejamos pasar y el gate del backend
      // (`PHONE_NOT_VERIFIED`) se encargará al crear la consulta.
      return true;
    }
  };

  // ─── Aplica los gates en cascada y arranca el flujo de booking ───
  // 1. Auth gate (híbrido) — sin login, abrimos AuthModal y guardamos pending.
  // 2. Phone gate (V2) — si phone_verified==false, abrimos WhatsApp modal.
  // 3. On-demand explainer — si el slot es bajo confirmación, mostramos modal.
  // 4. Validate gate — preguntamos al backend si la usuaria puede agendar como
  //    consulta incluida. Si no (sin membresía / sin minutos / membresía
  //    vencida), mostramos el BookingGateModal con las opciones correctas.
  // 5. Si la validación es exitosa, continúa al flujo de includedConfirmation.
  const proceedWithSlot = async (slot, userOverride = null) => {
    if (!slot) return;
    setSelectedSlot(slot);
    if (slot.init || slot.hour || slot.hourStart) {
      setSelectedTime(slot.init || slot.hour || slot.hourStart);
    }
    if (slot.date || slot.full_date) {
      const dateStr = slot.date || slot.full_date;
      setSelectedDate(new Date(dateStr + 'T12:00:00'));
    }

    const user = userOverride || loggedInUser;

    // Gate 1: login obligatorio antes de bookear (modo híbrido).
    if (!user) {
      setPendingSlotForBooking(slot);
      setShowAuthModal(true);
      return;
    }

    // Gate 2: phone verification (V2). No permitimos bloquear ningún horario
    // hasta que la usuaria verifique su WhatsApp.
    const phoneOk = await ensurePhoneVerified(user);
    if (!phoneOk) {
      setPhoneVerificationGate({ slot });
      return;
    }

    // Gate 3: explainer on-demand.
    const isOnDemand = slot.isOnDemand === true || slot.bookingMode === 'on_demand';
    if (isOnDemand) {
      setOnDemandSlotPending(slot);
      return;
    }

    // Gate 4: validación del backend (membresía / minutos / inscripción).
    // Si venimos del path post-login, propagamos el override de auth porque
    // `loggedInUser` puede no haberse hidratado todavía en el state de React.
    const slotForValidation = userOverride
      ? {
          ...slot,
          _overrideAuthToken: userOverride.authToken,
          _overrideUserId: userOverride.daisUserId || userOverride.userId,
        }
      : slot;
    runValidationGate(slotForValidation);
  };

  // Llama al endpoint Flutter `validate_schedule_include_consultation` y
  // decide qué hacer:
  //   - success === true  → flujo includedConfirmation (gratis con membresía)
  //   - success === false → BookingGateModal con opciones (comprar / pagar)
  // El backend es la fuente de verdad — más fiable que loggedInUser.membership.
  const runValidationGate = async (slot) => {
    // Overrides usados cuando el slot viene del flujo post-login (la sesión
    // está fresca pero loggedInUser aún no aterrizó en el state de React).
    const authToken = slot?._overrideAuthToken || loggedInUser?.authToken;
    const userIdNum = slot?._overrideUserId || loggedInUser?.daisUserId || loggedInUser?.userId;
    const doctorId = slot?.doctor?.id || slot?.professional_id || slot?.professional_ids?.[0]?.id || slot?.professional_ids?.[0];
    const durationId = slot?.durationId || slot?.duration_id;
    const scheduleId = slot?.scheduleId || slot?.id;
    const date = slot?.date || slot?.full_date;

    if (!authToken || !userIdNum || !doctorId || !durationId) {
      // Si faltan params críticos, caemos al checkout como fallback (es lo
      // que hacía la web antes — pero el path debería ser raro).
      console.warn('[runValidationGate] missing params, falling back to checkout', { userIdNum, doctorId, durationId });
      setStep('checkout');
      return;
    }

    setGlobalLoading(true);
    try {
      const validation = await scheduleService.validateScheduleInclude({
        authToken,
        userId: userIdNum,
        doctorId,
        date,
        durationId,
        scheduleId,
      });

      // Determinar el "gate kind" según los flags ESPECÍFICOS del backend.
      // Si NINGÚN flag aplica, la respuesta es ambigua: dejamos pasar al flujo
      // de membresía. El backend rechazará al crear la consulta si aplica
      // (`PHONE_NOT_VERIFIED`, sin permisos, etc.) y eso se maneja en
      // IncludedConsultationConfirm. Antes mostrábamos un modal engañoso
      // "Necesitas una membresía" cuando la usuaria SÍ la tenía.
      let gateKind = null;
      if (validation.withoutMembership || validation.withoutInscription) {
        gateKind = 'without_membership';
      } else if (validation.preferentialRequiresMembership) {
        gateKind = 'preferential';
      } else if (validation.membershipEndsBefore) {
        gateKind = 'membership_ends_before';
      } else if (validation.withoutMinutes) {
        gateKind = 'without_minutes';
      }

      if (validation.success || !gateKind) {
        // OK o ambiguo: proceder al flujo de membresía.
        handleMembershipSlotSelect();
      } else if (gateKind === 'without_membership') {
        // Sin membresía: una sola hoja "¿Cómo quieres agendar?" (MembershipOffer
        // en el checkout). Quitamos el BookingGateModal redundante para no pedir
        // dos veces lo mismo; cualquier horario es agendable pagando esa cita
        // (paridad con la app). El modal queda sólo para casos de miembro
        // (sin minutos / vence antes) y para horarios preferenciales.
        setStep('checkout');
      } else {
        setBookingGate({ slot, validation, gateKind });
      }
    } catch (err) {
      console.error('[runValidationGate] unexpected error:', err);
      // Fallback seguro: enviar a checkout (paga la consulta) para no bloquear.
      setStep('checkout');
    } finally {
      setGlobalLoading(false);
    }
  };

  // Handlers del BookingGateModal:
  const handleGateChooseMembership = () => {
    setBookingGate(null);
    // No pre-seleccionamos plan: MembershipCheckout muestra el selector
    // embebido con las 3 opciones (mensual / trimestral / anual) y la usuaria
    // decide cuál comprar antes de pagar.
    setSelectedMembership(null);
    setStep('membershipCheckout');
  };

  const handleGateChoosePayExtra = () => {
    setBookingGate(null);
    // Flujo de pago directo de la consulta (CheckoutFlow). El slot ya está
    // en estado, así que CheckoutFlow tiene todo lo que necesita.
    setStep('checkout');
  };

  const handleGateClose = () => {
    setBookingGate(null);
    setSelectedSlot(null);
  };

  const handleDoctorSelect = (slot) => {
    // En el modo "Por doctora" guardamos referencia al doctor para mostrarlo
    // en pantallas posteriores (BookingForm / Confirmación / ConfirmationScreen).
    if (slot?.doctor) setSelectedDoctor(slot.doctor);
    proceedWithSlot(slot);
  };

  const handleConditionSelect = (slot) => {
    // El nuevo ConditionSelection ya entrega un slot completo (doctora + horario).
    if (slot?.doctor) setSelectedDoctor(slot.doctor);
    proceedWithSlot(slot);
  };

  const handleDateSelect = (date) => {
    if (!selectedSpecialty) return;
    setSelectedDate(date);
  };

  const handleTimeClick = (slot) => proceedWithSlot(slot);

  // Confirmación del explainer on-demand: el usuario acepta. Un slot bajo
  // confirmación NO requiere membresía (paridad con flutterUXS): el backend
  // `create_include_consultation_by_user` detecta el booking_mode on_demand y
  // crea una solicitud `pending_doctor_confirmation` SIN cobro ni check de
  // membresía. Por eso NO pasamos por `runValidationGate` (que exige membresía
  // y desemboca en el pago extra). Vamos directo al paso de motivo → create.
  const handleOnDemandAccept = () => {
    const slot = onDemandSlotPending;
    setOnDemandSlotPending(null);
    if (slot) handleMembershipSlotSelect();
  };
  const handleOnDemandCancel = () => {
    setOnDemandSlotPending(null);
    setSelectedSlot(null);
  };

  // Tras verificar WhatsApp en el gate pre-booking, retomamos proceedWithSlot
  // con el flag local actualizado para que ahora sí pase el gate.
  const handlePhoneGateVerified = () => {
    const slot = phoneVerificationGate?.slot;
    setPhoneVerificationGate(null);
    setLoggedInUser(prev => prev ? { ...prev, phoneVerified: true } : prev);
    if (slot) {
      // Pequeño delay para que el setState propague antes del próximo gate.
      setTimeout(() => {
        proceedWithSlot(slot, { ...(loggedInUser || {}), phoneVerified: true });
      }, 50);
    }
  };

  const handlePhoneGateCancel = () => {
    setPhoneVerificationGate(null);
    setSelectedSlot(null);
  };

  const getFilteredTimeSlots = () => {
    if (!selectedDate) return [];
    const formattedDate = selectedDate.toISOString().split('T')[0];
    const slots = availabilityData[formattedDate] || [];
    
    if (selectedTimeSlot) {
      return slots.filter(slot => {
        const hour = parseInt(slot.init.split(':')[0]);
        if (selectedTimeSlot === 'manana') return hour >= 7 && hour < 12;
        if (selectedTimeSlot === 'tarde') return hour >= 12 && hour < 18;
        if (selectedTimeSlot === 'noche') return hour >= 18 && hour <= 22;
        return true;
      });
    }
    
    return slots;
  };

  const handleFormSubmit = (formResponse) => {
    
    if (formResponse.success) {
      setFormData(prev => ({
        ...prev,
        nombre: formResponse.nombre,
        apellido: formResponse.apellido,
        email: formResponse.email,
        password: formResponse.password,
        telefono: formResponse.telefono,
        phoneCountry: formResponse.phoneCountry || 'MX',
        phoneCode: formResponse.phoneCode || '+52',
        birthDate: formResponse.birthDate,
        birthSex: formResponse.birthSex,
        pronoun: formResponse.pronoun,
        consultationReason: formResponse.consultationReason
      }));
      
      setLeadData({
        leadId: formResponse.leadId,
        userId: formResponse.userId || null,
        firebaseUid: formResponse.firebaseUid || null,
        authToken: formResponse.authToken || null,
        accessToken: formResponse.accessToken || null,
        email: formResponse.email,
        password: formResponse.password
      });

      // Meta Pixel: registro completado. Sin parámetros: nada de PII (salud).
      pixelCompleteRegistration();

      setStep('checkout');
      
      Swal.fire({
        icon: 'success',
        title: '¡Datos guardados exitosamente!',
        text: 'Ahora puedes proceder con tu pago.',
        confirmButtonColor: '#786ED9',
        timer: 2000,
        showConfirmButton: false
      });
      
    } else {
      alert('Hubo un problema al guardar tus datos. Intenta de nuevo.');
    }
  };

  const handlePaymentComplete = (result) => {
    setPaymentResult(result);
    setStep('confirmation');

    // Meta Pixel: cargo de consulta confirmado + consulta agendada. Sin
    // especialidad en los parámetros (salud); eventID uuid para deduplicar
    // contra la Conversions API cuando Odoo mande el evento server-side.
    pixelSchedule();
    pixelPurchase({
      value: Number(result?.amount) || 0,
      currency: 'MXN',
      plan: 'Consulta individual',
      eventId: generateEventId(),
    });

    // Guardar reservación localmente
    const specConfig = specialtyConfig[selectedSpecialty];
    const dateStr = selectedDate ? selectedDate.toISOString().split('T')[0] : '';
    saveReservation({
      specialtyId: selectedSpecialty,
      specialtyName: specConfig?.name || '',
      specialtyIcon: specConfig?.icon || '',
      date: dateStr,
      time: selectedTime,
      doctorName: selectedSlot?.professional_name || ''
    });
    setSavedReservation(getReservation());
  };

  // ─── Included-consultation completed (no payment) ───
  const handleIncludedConsultationComplete = (result) => {
    // Si el slot era bajo confirmación, el backend devolvió la consulta en
    // estado pending_doctor_confirmation: la pantalla de confirmación debe
    // decir "solicitud enviada", no "consulta agendada".
    const isOnDemand =
      result?.data?.state === 'pending_doctor_confirmation' ||
      result?.data?.requires_doctor_confirmation === true;
    setPaymentResult({
      success: true,
      type: 'included',
      isOnDemand,
      deadline: result?.data?.doctor_confirmation_deadline || null,
      ...result,
    });
    setStep('confirmation');

    // Meta Pixel: consulta (incluida, sin pago) agendada.
    pixelSchedule();

    const specConfig = specialtyConfig[selectedSpecialty];
    const dateStr = selectedDate ? selectedDate.toISOString().split('T')[0] : '';
    saveReservation({
      specialtyId: selectedSpecialty,
      specialtyName: specConfig?.name || '',
      specialtyIcon: specConfig?.icon || '',
      date: dateStr,
      time: selectedTime,
      doctorName: selectedSlot?.professional_name || ''
    });
    setSavedReservation(getReservation());
    refreshUserData();
  };

  const resetBooking = () => {
    setStep('hero');
    setSelectedSpecialty('');
    setSelectedTimeSlot('');
    setSelectedDate(null);
    setSelectedTime('');
    setSelectedSlot(null);
    setSelectedDoctor(null);
    setAvailabilityData({});
    setShowConfirmation(false);
    setAcceptedTerms(false);
    setValidationErrors({ email: '', telefono: '' });
    setLeadData({
      leadId: null,
      email: '',
      password: ''
    });
    setBookingMethod('');
    setPaymentResult(null);
    setSelectedMembership(null);
  };

  const handleSelectMembership = (plan) => {
    if (!loggedInUser) {
      // Save the plan so we can navigate after login
      setSelectedMembership(plan);
      setShowAuthModal(true);
      return;
    }
    setSelectedMembership(plan);
    setStep('membershipCheckout');
  };

  const handleLogout = () => {
    clearSession();
    setLoggedInUser(null);
    setSavedReservation(null);
    resetBooking();
  };

  const handleStartVerification = () => {
    setStep('verification');
  };

  const handleOpenProfile = () => {
    window.history.pushState({}, '', '/perfil');
    setCurrentRoute('perfil');
  };

  const handleLoginClick = () => {
    Swal.fire({
      title: '¿Dónde quieres iniciar sesión?',
      html: '<p style="color:#666; margin-bottom: 8px;">Elige cómo prefieres acceder a tu cuenta Daisies</p>',
      showCancelButton: true,
      confirmButtonText: 'Ir a la App',
      cancelButtonText: 'Iniciar aquí',
      confirmButtonColor: '#786ED9',
      cancelButtonColor: '#9F94E8',
      reverseButtons: true,
      customClass: {
        popup: 'rounded-2xl',
        confirmButton: 'rounded-xl px-6',
        cancelButton: 'rounded-xl px-6'
      }
    }).then((result) => {
      if (result.isConfirmed) {
        window.open('https://app.daisies.mx/login', '_blank');
      } else if (result.dismiss === Swal.DismissReason.cancel) {
        setShowAuthModal(true);
      }
    });
  };

  const handleAuthSuccess = async (opts = {}) => {
    const isNewUser = !!opts?.isNewUser;
    setShowAuthModal(false);
    const session = getUserSession();
    if (session) {
      let enrichedSession = { ...session };

      // Enriquecer con datos de dais_user
      if (session.firebaseUid) {
        try {
          const daisUser = await fetchDaisUserByFirebaseUid(session.firebaseUid);
          if (daisUser) {
            enrichedSession = {
              ...enrichedSession,
              nombre: daisUser.name || session.nombre,
              apellido: daisUser.last_name || session.apellido,
              email: daisUser.email || session.email,
              telefono: daisUser.phone || session.telefono,
              avatar: daisUser.avatar || '',
              daisUserId: daisUser.id || null,
              stepState: daisUser.step_state || '',
              membership: daisUser.membership || null,
              membership_active: daisUser.membership_active || '',
              inscription: daisUser.inscription || null,
              inscription_active: daisUser.inscription_active || '',
              balance: daisUser.balance ?? null,
              phoneVerified: session?.phoneVerified === true || daisUser.phone_verified === true,
            };

            // Obtener consultas del usuario
            if (daisUser.id) {
              const consultations = await fetchUserConsultations(daisUser.id);
              setUserConsultations(consultations);
            }
          }
        } catch (err) {
          console.warn('Error enriching session after login:', err);
        }
      }

      setLoggedInUser(enrichedSession);
      setSavedReservation(getReservation());

      // If user was trying to buy a membership, navigate there now
      if (selectedMembership) {
        setStep('membershipCheckout');
        return;
      }

      // Si había un slot pendiente (gate híbrido), retomamos el flujo de booking
      // pasando por el mismo proceedWithSlot (con user override porque el state
      // de React aún no se rehidrató). Esto re-aplica los gates en cascada:
      // phone verification → on-demand → validation backend.
      if (pendingSlotForBooking) {
        const slot = pendingSlotForBooking;
        setPendingSlotForBooking(null);
        setTimeout(() => {
          proceedWithSlot(slot, enrichedSession);
        }, 50);
        return;
      }

      // Usuario recién registrado → onboarding breve antes de soltarlo en el dashboard
      if (isNewUser) {
        setStep('welcomeOnboarding');
        return;
      }
    }

    if (!isNewUser) {
      Swal.fire({
        icon: 'success',
        title: '¡Bienvenida de vuelta!',
        text: 'Has iniciado sesión exitosamente.',
        confirmButtonColor: '#786ED9',
        timer: 2000,
        showConfirmButton: false
      });
    }
  };

  // ── Refresh user data from Firestore (call after any transaction) ──
  const refreshUserData = async () => {
    const session = getUserSession();
    if (!session?.firebaseUid) return;
    try {
      const daisUser = await fetchDaisUserByFirebaseUid(session.firebaseUid);
      if (daisUser) {
        const enriched = {
          ...session,
          nombre: daisUser.name || session.nombre,
          apellido: daisUser.last_name || session.apellido,
          email: daisUser.email || session.email,
          telefono: daisUser.phone || session.telefono,
          avatar: daisUser.avatar || '',
          daisUserId: daisUser.id || null,
          stepState: daisUser.step_state || '',
          membership: daisUser.membership || null,
          membership_active: daisUser.membership_active || '',
          inscription: daisUser.inscription || null,
          inscription_active: daisUser.inscription_active || '',
          balance: daisUser.balance ?? null,
          phoneVerified: session?.phoneVerified === true || daisUser.phone_verified === true,
        };
        setLoggedInUser(enriched);
        if (daisUser.id) {
          const consultations = await fetchUserConsultations(daisUser.id);
          setUserConsultations(consultations);
        }
      }
    } catch (err) {
      console.warn('Error refreshing user data:', err);
    }
  };

  // Override optimista del stepState — para usar tras avanzar la verificación de email/teléfono.
  // El stepState en Firestore puede tardar 1-3s en sincronizarse desde Odoo, así que
  // actualizamos local primero (banner desaparece) y luego refreshUserData con retry confirma.
  const setLocalStepState = (newStepState) => {
    setLoggedInUser(prev => prev ? { ...prev, stepState: newStepState } : prev);
    // Retry de refresh después de un par de segundos para alinear con Firestore
    setTimeout(() => { refreshUserData(); }, 1500);
    setTimeout(() => { refreshUserData(); }, 4000);
  };

  const handleOndemandRequest = (doctor) => {
    // If not logged in, show auth modal first
    if (!loggedInUser) {
      setSelectedOndemandDoctor(doctor);
      setShowAuthModal(true);
      return;
    }
    setSelectedOndemandDoctor(doctor);
    setStep('ondemand');
  };

  const handleOndemandSuccess = (result) => {
    // After success, we could go back to hero or a dashboard
    setStep('hero');
    refreshUserData();
  };

  const filteredTimeSlots = getFilteredTimeSlots();

  const renderStep = () => {
    switch (step) {
      case 'hero':
        return (
          <HeroSection
            onStartBooking={(specialtyId) => {
              // Solo aceptamos un specialtyId si es número o string numérico (defensa
              // contra handlers que pasan SyntheticEvent al click de botones genéricos
              // tipo "Agendar consulta" del dashboard).
              const idNum = parseInt(specialtyId);
              if (!Number.isNaN(idNum) && idNum > 0) {
                setSelectedSpecialty(idNum);
                setStep('bookingMethod');
              } else {
                setStep('specialty');
              }
            }}
            onRouteStartBooking={handleRouteStartBooking}
            onSelectMembership={handleSelectMembership}
            loggedInUser={loggedInUser}
            reservation={savedReservation}
            consultations={userConsultations}
            onLogout={handleLogout}
            onLoginClick={handleLoginClick}
            onStartVerification={handleStartVerification}
            onOpenProfile={handleOpenProfile}
          />
        );

      case 'specialty':
        return (
          <SpecialtySelection
            specialties={specialtiesLoading ? [] : filteredSpecialties}
            selectedSpecialty={selectedSpecialty}
            onSelect={handleSpecialtySelect}
            onBack={() => setStep('hero')}
          />
        );
      
      case 'bookingMethod': {
        const bookingMethodSpec = getSpecialtyConfig();
        return (
          <BookingMethodSelector
            onSelect={handleBookingMethodSelect}
            onBack={() => setStep('specialty')}
            selectedSpecialty={bookingMethodSpec}
            onChangeSpecialty={() => setStep('specialty')}
          />
        );
      }
      
      case 'doctorSelection': {
        const doctorSpecialtyConfig = getSpecialtyConfig();

        // Si no hay config, regresamos a la selección de especialidad en lugar de
        // forzar Psicología. Esto evita el bug de "elegí dermatología y agendé otra".
        if (!doctorSpecialtyConfig) {
          return (
            <div className="max-w-md mx-auto px-4 py-12 text-center">
              <h3 className="text-lg font-semibold text-text-primary mb-2">
                No encontramos esa especialidad
              </h3>
              <p className="text-text-secondary text-sm mb-6">
                Por favor selecciona una especialidad nuevamente.
              </p>
              <button
                onClick={() => setStep('specialty')}
                className="press-scale px-6 py-3 bg-daisies text-white rounded-full font-semibold hover:bg-daisies-hover transition-all"
              >
                Elegir especialidad
              </button>
            </div>
          );
        }

        return (
          <DoctorSelection
            specialty={doctorSpecialtyConfig}
            onSelect={handleDoctorSelect}
            onBack={() => setStep('bookingMethod')}
            availabilityData={availabilityData}
            utmParams={utmParams}
            allSpecialties={specialties}
            allSpecialtyConfig={specialtyConfig}
            campaignDurationMap={campaignDurationMap}
            onOndemandRequest={handleOndemandRequest}
            loggedInUser={loggedInUser}
          />
        );
      }

      case 'conditionSelection': {
        const conditionSpec = getSpecialtyConfig();
        return (
          <ConditionSelection
            onSelect={handleConditionSelect}
            onBack={() => setStep('bookingMethod')}
            selectedSpecialty={conditionSpec}
            loggedInUser={loggedInUser}
            onOndemandRequest={handleOndemandRequest}
          />
        );
      }

      case 'calendar': {
        const calendarSpec = getSpecialtyConfig();
        return (
          <CalendarSelection
            selectedDate={selectedDate}
            selectedTimeSlot={selectedTimeSlot}
            selectedSlot={selectedSlot}
            onDateSelect={handleDateSelect}
            onTimeClick={handleTimeClick}
            onBack={() => setStep('bookingMethod')}
            onSelectOtherDate={() => setSelectedDate(null)}
            setSelectedTimeSlot={setSelectedTimeSlot}
            setSelectedSlot={setSelectedSlot}
            hasMembership={loggedInUser?.membership?.state === 'active' || loggedInUser?.membership_active ? true : false}
            specialty={calendarSpec}
            authToken={loggedInUser?.authToken || ''}
            userId={loggedInUser?.daisUserId || loggedInUser?.userId}
          />
        );
      }
      
      case 'form':
        return (
          <BookingForm
            formData={formData}
            setFormData={setFormData}
            validationErrors={validationErrors}
            setValidationErrors={setValidationErrors}
            acceptedTerms={acceptedTerms}
            setAcceptedTerms={setAcceptedTerms}
            loading={loading}
            selectedSpecialty={selectedSpecialty}
            selectedDate={selectedDate}
            selectedTime={selectedTime}
            selectedSlot={selectedSlot}
            specialtyConfig={specialtyConfig}
            utmParams={utmParams}
            selectedDoctor={selectedDoctor}
            onSubmit={handleFormSubmit}
            onBack={() => setStep('calendar')}
          />
        );
      
      case 'checkout':
        return (
          <CheckoutFlow
            userData={{
              ...formData,
              ...leadData,
              nombre: formData.nombre,
              apellido: formData.apellido,
              email: formData.email,
              telefono: formData.telefono,
              birthDate: formData.birthDate,
              birthSex: formData.birthSex,
              pronoun: formData.pronoun
            }}
            selectedSpecialty={selectedSpecialty}
            selectedDate={selectedDate}
            selectedTime={selectedTime}
            selectedSlot={selectedSlot}
            specialtyConfig={specialtyConfig}
            selectedDoctor={selectedDoctor}
            onPaymentComplete={handlePaymentComplete}
            onBack={() => setStep('form')}
          />
        );

      case 'membershipCheckout':
        return (
          <MembershipCheckout
            membershipPlan={selectedMembership || {}}
            userId={loggedInUser?.daisUserId || loggedInUser?.userId || localStorage.getItem('daisies_user_id')}
            onSuccess={(result) => {
              setSelectedMembership(null);
              resetBooking();
              refreshUserData();
            }}
            onBack={() => {
              setSelectedMembership(null);
              setStep('hero');
            }}
          />
        );
      
      case 'confirmation':
        return (
          <ConfirmationScreen
            formData={formData}
            selectedSpecialty={selectedSpecialty}
            selectedDate={selectedDate}
            selectedTime={selectedTime}
            specialtyConfig={specialtyConfig}
            selectedSlot={selectedSlot}
            paymentResult={paymentResult}
            onCompleteMedicalRecord={handleCompleteMedicalRecord}
            onStartVerification={handleStartVerification}
            onReset={() => {
              resetBooking();
              refreshUserData();
            }}
          />
        );

      case 'survey':
        return (
          <SurveyFlow
            specialtyId={getSpecialtyConfig()?.id || parseInt(selectedSpecialty)}
            surveyId={pendingSurveyId}
            authToken={loggedInUser?.authToken}
            userId={loggedInUser?.daisUserId || loggedInUser?.userId}
            onComplete={() => setStep('includedConfirmation')}
            onBack={() => setStep('calendar')}
          />
        );

      case 'includedConfirmation':
        return (
          <IncludedConsultationConfirm
            selectedSlot={selectedSlot}
            selectedDate={selectedDate}
            selectedTime={selectedTime}
            specialtyConfig={specialtyConfig}
            selectedSpecialty={selectedSpecialty}
            loggedInUser={loggedInUser}
            campaignDurationMap={campaignDurationMap}
            allSpecialties={specialties}
            onComplete={handleIncludedConsultationComplete}
            onBack={() => setStep('calendar')}
          />
        );

      case 'verification':
        return (
          <VerificationFlow
            userData={{
              ...formData,
              ...leadData,
              userId: loggedInUser?.daisUserId || loggedInUser?.userId || leadData?.userId || localStorage.getItem('daisies_user_id'),
              email: loggedInUser?.email || formData.email,
              telefono: loggedInUser?.telefono || formData.telefono,
              stepState: loggedInUser?.stepState || ''
            }}
            onStepAdvance={setLocalStepState}
            onComplete={() => {
              setStep('hero');
              refreshUserData();
            }}
            onBack={() => setStep('hero')}
          />
        );
      
      case 'welcomeOnboarding':
        return (
          <WelcomeOnboarding
            user={loggedInUser}
            onSelectMembership={() => {
              const plans = getMembershipPlans(getSpecialtyPrice());
              const monthly = plans?.monthly;
              if (monthly) {
                setSelectedMembership(monthly);
                setStep('membershipCheckout');
              } else {
                setStep('hero');
              }
            }}
            onGoHome={() => setStep('hero')}
          />
        );

      case 'ondemand':
        return (
          <RequestOndemandPage
            doctor={selectedOndemandDoctor}
            specialty={getSpecialtyConfig()}
            loggedInUser={loggedInUser}
            onBack={() => setStep('doctorSelection')}
            onSuccess={handleOndemandSuccess}
          />
        );
      
      default:
        return (
          <HeroSection
            onStartBooking={(specialtyId) => {
              const idNum = parseInt(specialtyId);
              if (!Number.isNaN(idNum) && idNum > 0) {
                setSelectedSpecialty(idNum);
                setStep('bookingMethod');
              } else {
                setStep('specialty');
              }
            }}
            onRouteStartBooking={handleRouteStartBooking}
            onSelectMembership={handleSelectMembership}
            loggedInUser={loggedInUser}
            reservation={savedReservation}
            consultations={userConsultations}
            onLogout={handleLogout}
            onLoginClick={handleLoginClick}
            onStartVerification={handleStartVerification}
          />
        );
    }
  };

  // ============ ROUTE-LEVEL CALLBACK: jump into booking from /ahora or /horarios ============
  const handleRouteStartBooking = async (specialtyId, slotData) => {
    const idNum = specialtyId ? parseInt(specialtyId) : null;
    if (idNum) {
      setSelectedSpecialty(idNum);
    }
    if (slotData) {
      setSelectedSlot(slotData);
      setSelectedTime(slotData.init || slotData.time);
      if (slotData.date) setSelectedDate(new Date(slotData.date + 'T12:00:00'));

      // Resolve doctor info from professional_ids for display in BookingForm / MembershipOffer
      const doctorId = slotData.professional_ids?.[0]?.id || slotData.professional_ids?.[0];
      if (doctorId && idNum) {
        try {
          const doctors = await doctorService.getDoctorsBySpecialty(idNum);
          const matched = doctors.find(d => d.id === doctorId);
          if (matched) setSelectedDoctor(matched);
        } catch (err) {
          console.warn('[handleRouteStartBooking] Could not resolve doctor info:', err);
        }
      }
    }
    // switch back to the main app flow
    window.history.pushState({}, '', '/');
    setCurrentRoute('main');
    if (slotData) {
      // Mismos gates que el flujo normal: login → on-demand → checkout/membership.
      proceedWithSlot(slotData);
    } else {
      setStep('bookingMethod');
    }
  };

  // ============ ROUTE-LEVEL RENDERING ============
  if (currentRoute === 'ahora') {
    return (
      <div className="min-h-screen bg-warm-canvas">
        <DoctorDirectoryPage 
          onStartBooking={handleRouteStartBooking} 
          onLoginClick={handleLoginClick} 
          onOndemandRequest={handleOndemandRequest}
        />
        <AuthModal
          isOpen={showAuthModal}
          onClose={() => setShowAuthModal(false)}
          onSuccess={handleAuthSuccess}
          initialStep="login"
          utmParams={utmParams}
        />
      </div>
    );
  }

  if (currentRoute === 'horarios-doctora') {
    return (
      <DoctorScheduleFullPage
        onSlotClick={(specialtyId, slot) => {
          // Navega a /agendar-match con el slot elegido
          const params = new URLSearchParams({
            doctor: slot.doctor?.id || slot.professional_ids?.[0]?.id || '',
            specialty: slot.doctor?.specialty?.name || '',
            slot_date: slot.date || '',
            slot_hour: slot.init || slot.time || '',
            slot_mode: slot.booking_mode || 'immediate',
            duration_id: slot.duration_id || '',
          });
          window.history.pushState({}, '', `/agendar-match?${params.toString()}`);
          setCurrentRoute('agendar-match');
        }}
        onBackToDirectory={() => {
          const urlParams = new URLSearchParams(window.location.search);
          const doctorId = urlParams.get('id');
          const specialty = urlParams.get('specialty');
          const target = new URLSearchParams();
          if (specialty) target.set('specialty', specialty);
          if (doctorId) target.set('doctor', doctorId);
          window.history.pushState({}, '', `/ahora${target.toString() ? '?' + target.toString() : ''}`);
          setCurrentRoute('ahora');
        }}
      />
    );
  }

  if (currentRoute === 'agendar-match') {
    return (
      <MatchConfirmationPage
        onConfirm={(slot, dr, spec) => {
          // Setea state global y delega al flow existente proceedWithSlot.
          setSelectedSpecialty(spec.id);
          setSelectedDoctor(dr);
          setSelectedSlot(slot);
          setSelectedTime(slot.init || slot.time);
          if (slot.date) setSelectedDate(new Date(slot.date + 'T12:00:00'));
          window.history.pushState({}, '', '/');
          setCurrentRoute('main');
          proceedWithSlot(slot);
        }}
        onMoreSchedules={(doctorIdParam, specialtyName) => {
          const target = new URLSearchParams();
          if (doctorIdParam) target.set('id', doctorIdParam);
          if (specialtyName) target.set('specialty', specialtyName);
          window.history.pushState({}, '', `/horarios-doctora?${target.toString()}`);
          setCurrentRoute('horarios-doctora');
        }}
        onProposeSlot={(dr) => {
          if (handleOndemandRequest) handleOndemandRequest(dr);
        }}
      />
    );
  }

  if (currentRoute === 'perfil') {
    return (
      <div className="min-h-screen bg-warm-canvas">
        <ProfilePage
          user={loggedInUser}
          onBack={() => {
            window.history.pushState({}, '', '/');
            setCurrentRoute('main');
          }}
          onLogout={handleLogout}
          onSelectMembership={handleSelectMembership}
          onUserUpdate={(partial) =>
            setLoggedInUser((prev) => (prev ? { ...prev, ...partial } : prev))
          }
        />
      </div>
    );
  }

  // /horarios now redirects to main landing (schedule is embedded in home)
  if (currentRoute === 'horarios') {
    window.history.replaceState({}, '', '/');
    setCurrentRoute('main');
  }

  return (
    <div className="min-h-screen bg-warm-canvas">
      {/* ============ LOADER GLOBAL CON FLOR GIRATORIA ============ */}
      {globalLoading && (
        <GlobalLoader 
          title="Cargando horarios" 
          subtitle="Estamos buscando los mejores horarios para ti" 
        />
      )}

      {renderStep()}

      {/* Auth Modal for browser login */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => {
          setShowAuthModal(false);
          // Si la usuaria cerró el modal sin loguearse, limpiamos el pending slot
          // para que no quede colgado para el siguiente intento.
          setPendingSlotForBooking(null);
        }}
        onSuccess={(opts) => {
          // IMPORTANTE: forward opts ({isNewUser: true} tras registro) para que
          // handleAuthSuccess pueda routear al onboarding morado en lugar del dashboard.
          handleAuthSuccess(opts);
          // If they were trying to do SOS, jump there now (solo aplica al login)
          if (!opts?.isNewUser && selectedOndemandDoctor) {
            setStep('ondemand');
          }
        }}
        initialStep="login"
        utmParams={utmParams}
      />

      {/* Modal explainer on-demand (Flutter parity) */}
      {onDemandSlotPending && (
        <OnDemandExplainerModal
          slot={onDemandSlotPending}
          doctor={onDemandSlotPending.doctor || selectedDoctor}
          onAccept={handleOnDemandAccept}
          onCancel={handleOnDemandCancel}
        />
      )}

      {/* Booking gate: aparece SOLO cuando un flag específico del backend
          aplica (sin membresía, sin inscripción, sin minutos, membresía
          vence, slot preferencial). Si la validación es ambigua, el flujo
          normal sigue sin bloqueo. */}
      {bookingGate && (
        <BookingGateModal
          slot={bookingGate.slot}
          validation={bookingGate.validation}
          gateKind={bookingGate.gateKind}
          onChooseMembership={handleGateChooseMembership}
          onChoosePayExtra={handleGateChoosePayExtra}
          onClose={handleGateClose}
        />
      )}

      {/* Phone verification gate V2 — bloquea ANTES de bookear ningún slot.
          Cuando la usuaria verifica su WhatsApp, retomamos proceedWithSlot
          con el slot pendiente. */}
      {phoneVerificationGate && (
        <WhatsappVerificationModal
          userId={loggedInUser?.daisUserId || loggedInUser?.userId}
          authToken={loggedInUser?.authToken}
          phone={loggedInUser?.telefono || loggedInUser?.phone}
          onVerified={handlePhoneGateVerified}
          onCancel={handlePhoneGateCancel}
        />
      )}
    </div>
  );
};

export default App;